import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import HeroVisual from "./HeroVisual";

function Spotlight({
  id,
  eyebrow,
  title,
  body,
  points,
  cta,
  ctaHref,
  visual,
  reverse,
}) {
  return (
    <section id={id} className="relative overflow-hidden py-20 lg:py-28">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          {/* Copy — slides in from the side it sits on, the visual from the
              opposite side, so the pair converges toward the centre. */}
          <div
            className={`lp-reveal ${reverse ? "lp-right lg:order-2" : "lp-left"} max-w-xl`}
          >
            <div className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-(--color-primary)">
              <span className="h-px w-6 bg-(--color-primary)" />
              {eyebrow}
            </div>
            <h2 className="qv-glitch text-[32px] sm:text-[40px] font-medium leading-tight tracking-tight text-(--color-text-primary)">
              {title}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-(--color-text-secondary)">
              {body}
            </p>

            <ul className="mt-7 space-y-3.5">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-(--color-primary-100) text-(--color-primary)">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-[14px] leading-relaxed text-(--color-text-secondary)">
                    {point}
                  </span>
                </li>
              ))}
            </ul>

            <Link
              to={ctaHref}
              className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-(--color-primary)"
            >
              {cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Visual */}
          <div className={`lp-reveal ${reverse ? "lp-left" : "lp-right"}`}>
            {visual}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Topology: three floating panels ─────────────────────────────────────── */

const PANEL_STYLE = {
  // opaque (token tint over a solid base) so overlapping panels never show
  // through each other, plus depth glow.
  background:
    "linear-gradient(160deg, var(--color-bg-card), var(--color-bg-card)), #0c1310",
  boxShadow:
    "0 40px 80px -34px rgba(0,0,0,0.85), 0 0 46px -26px var(--color-shadow-primary)",
};

function Panel({ tilt, children }) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-(--color-border)"
      style={{
        ...PANEL_STYLE,
        transform: `perspective(1600px) rotateY(${tilt})`,
      }}
    >
      {children}
    </div>
  );
}

function PanelBar({ title, status, dots = true }) {
  return (
    <div className="flex items-center gap-2 border-b border-(--color-border) px-3 py-2.5">
      {dots && (
        <span className="flex gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]/80" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]/80" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]/80" />
        </span>
      )}
      <span className="qv-mono text-[10.5px] text-(--color-text-muted)">
        {title}
      </span>
      {status && (
        <span className="ml-auto flex items-center gap-1.5 qv-mono text-[10px] text-(--color-primary)">
          <span className="cyber-badge-dot" />
          {status}
        </span>
      )}
    </div>
  );
}

const DEVICES = [
  {
    name: "Cache Server",
    path: (
      <>
        <rect x="4" y="4" width="16" height="7" rx="1" />
        <rect x="4" y="13" width="16" height="7" rx="1" />
      </>
    ),
  },
  {
    name: "DNS Server",
    path: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18" />
      </>
    ),
  },
  { name: "Mail Server", path: <path d="M4 7h16M4 12h16M4 17h10" /> },
  {
    name: "Message Queue",
    path: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M8 9h8M8 13h5" />
      </>
    ),
  },
];

// Canvas topology — real devices and how they connect (coords in a 420×320 box).
const TOPO_NODES = [
  {
    id: "lb",
    label: "Load Balancer",
    x: 210,
    y: 54,
    live: true,
    icon: (
      <>
        <circle cx="12" cy="5" r="1.8" />
        <circle cx="6" cy="18" r="1.8" />
        <circle cx="18" cy="18" r="1.8" />
        <path d="M12 6.8v3.2M12 10H6.5v6.2M12 10h5.5v6.2" />
      </>
    ),
  },
  {
    id: "w1",
    label: "Web · 01",
    x: 130,
    y: 142,
    icon: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M4 9h16" />
      </>
    ),
  },
  {
    id: "w2",
    label: "Web · 02",
    x: 290,
    y: 142,
    icon: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M4 9h16" />
      </>
    ),
  },
  {
    id: "db",
    label: "Database",
    x: 150,
    y: 240,
    icon: (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.6" />
        <path d="M5 6v12c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6" />
      </>
    ),
  },
  {
    id: "cache",
    label: "Cache",
    x: 290,
    y: 240,
    live: true,
    icon: (
      <>
        <path d="M12 3l8 4-8 4-8-4 8-4z" />
        <path d="M4 12l8 4 8-4M4 16l8 4 8-4" />
      </>
    ),
  },
];
const TOPO_LINKS = [
  "M210,54 L130,142",
  "M210,54 L290,142",
  "M130,142 L290,142",
  "M130,142 L150,240",
  "M290,142 L290,240",
];

function TopologyVisual() {
  return (
    <div className="relative mx-auto h-130 w-full max-w-150 sm:h-140">
      {/* data-wire: chat → canvas */}
      <svg
        className="pointer-events-none absolute inset-0 z-2 h-full w-full"
        viewBox="0 0 600 560"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M180,372 C 230,330 250,290 300,268"
          fill="none"
          stroke="color-mix(in srgb, var(--color-primary) 55%, transparent)"
          strokeWidth="1.4"
          strokeDasharray="5 6"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-22"
            dur="1.2s"
            repeatCount="indefinite"
          />
        </path>
      </svg>

      {/* CANVAS (centred, largest, back) */}
      <div className="absolute left-1/2 top-0 z-1 w-115 max-w-[92%] -translate-x-1/2">
        <div className="lp-float">
          <Panel tilt="3deg">
            <PanelBar title="topology · canvas" status="BUILDING" />
            <div className="lp-topo-canvas relative h-80">
              <span
                aria-hidden="true"
                className="lp-topo-grid pointer-events-none absolute inset-0 opacity-50"
              />

              {/* connections + flowing packets */}
              <svg
                viewBox="0 0 420 320"
                className="absolute inset-0 h-full w-full"
              >
                {TOPO_LINKS.map((d) => (
                  <path
                    key={d}
                    d={d}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeOpacity="0.4"
                    strokeWidth="1.4"
                  />
                ))}
                <circle
                  r="2.6"
                  fill="var(--color-primary)"
                  style={{
                    filter: "drop-shadow(0 0 4px var(--color-primary))",
                  }}
                >
                  <animateMotion
                    dur="2.4s"
                    repeatCount="indefinite"
                    path="M210,54 L130,142"
                  />
                </circle>
                <circle
                  r="2.6"
                  fill="var(--color-primary)"
                  style={{
                    filter: "drop-shadow(0 0 4px var(--color-primary))",
                  }}
                >
                  <animateMotion
                    dur="2.9s"
                    repeatCount="indefinite"
                    path="M210,54 L290,142"
                  />
                </circle>
                <circle
                  r="2.6"
                  fill="var(--color-primary)"
                  style={{
                    filter: "drop-shadow(0 0 4px var(--color-primary))",
                  }}
                >
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    path="M290,142 L290,240"
                  />
                </circle>
              </svg>

              {/* device chips */}
              {TOPO_NODES.map((n) => (
                <div
                  key={n.id}
                  className="lp-topo-chip absolute z-2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg border px-2 py-1.5"
                  style={{
                    left: `${(n.x / 420) * 100}%`,
                    top: `${(n.y / 320) * 100}%`,
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3.5 w-3.5 text-(--color-primary)"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  >
                    {n.icon}
                  </svg>
                  <span className="whitespace-nowrap text-[9.5px] font-medium text-(--color-text-primary)">
                    {n.label}
                  </span>
                  {n.live && <span className="cyber-badge-dot ml-0.5" />}
                </div>
              ))}

              <div className="absolute bottom-2.5 left-3 qv-mono text-[10px] text-(--color-text-muted)">
                <span className="text-(--color-primary)">›</span> web-server
                topology · 5 nodes
              </div>
            </div>
          </Panel>
        </div>
      </div>

      {/* CHAT (front / bottom-left corner) */}
      <div className="absolute bottom-0 left-0 z-3 w-56 max-w-[48%]">
        <div className="lp-float" style={{ animationDelay: "-2.2s" }}>
          <Panel tilt="7deg">
            <PanelBar title="ai-architect" dots />
            <div className="p-2.5">
              <div className="mb-2 flex gap-1.5">
                <span className="rounded-md border border-(--color-primary)/40 bg-(--color-primary-50) px-2.5 py-1 text-[10.5px] text-(--color-primary)">
                  Ask
                </span>
                <span className="rounded-md border border-(--color-border) px-2.5 py-1 text-[10.5px] text-(--color-text-muted)">
                  Agent
                </span>
                <span className="rounded-md border border-(--color-border) px-2.5 py-1 text-[10.5px] text-(--color-text-muted)">
                  Plan
                </span>
              </div>
              <div className="mb-2.5 rounded-lg border border-(--color-border) bg-(--color-primary-50) p-2 text-[11px] leading-snug text-(--color-text-secondary)">
                Hello! Describe what you want to build and I'll generate it.
              </div>
              <div className="mb-1.5 qv-mono text-[9px] tracking-widest text-(--color-text-muted)">
                TRY ASKING
              </div>
              <div className="mb-1.5 rounded-lg border border-(--color-border) px-2.5 py-1.5 text-[10px] text-(--color-text-secondary)">
                Create a web-server topology with a load balancer
              </div>
              <div className="rounded-lg border border-(--color-border) px-2.5 py-1.5 text-[10px] text-(--color-text-secondary)">
                Design a secure network topology
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-(--color-border) py-1.5 pl-3 pr-1.5 qv-mono text-[8px] text-(--color-text-muted)">
                Describe your topology…
                <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-md bg-(--color-primary) text-(--color-text-on-primary)">
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </Panel>
        </div>
      </div>

      {/* DEVICES (front / bottom-right corner, aligned with the chat panel) */}
      <div className="absolute bottom-0 right-0 z-2 w-49 max-w-[44%]">
        <div className="lp-float" style={{ animationDelay: "-4.4s" }}>
          <Panel tilt="-7deg">
            <PanelBar title="device library" status="42" />
            <div className="p-3">
              <div className="mb-3 rounded-lg border border-(--color-border) px-2.5 py-2 qv-mono text-[10.5px] text-(--color-text-muted)">
                Search devices…
              </div>
              <div className="mb-2 qv-mono text-[8.5px] tracking-widest text-(--color-text-muted)">
                APPLICATION SERVICES
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEVICES.map((d) => (
                  <div
                    key={d.name}
                    className="flex cursor-grab flex-col items-center gap-1.5 rounded-lg border border-(--color-border) px-1.5 py-2.5 text-center transition-colors hover:border-(--color-primary)"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-(--color-primary-50) text-(--color-primary)">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                      >
                        {d.path}
                      </svg>
                    </span>
                    <span className="text-[9px] leading-tight text-(--color-text-muted)">
                      {d.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default function Spotlights() {
  return (
    <>
      <Spotlight
        id="cyber-range"
        eyebrow="Cyber Range Engine"
        title={
          <>
            Spin up isolated ranges in{" "}
            <span className="lp-highlight">seconds</span>, not sprints
          </>
        }
        body="Every scenario provisions its own attack box and secure tunnel. No VMs to babysit, no lab to rebuild — request a range, get a VPN certificate, and start hacking from the browser."
        points={[
          "One-click attack boxes provisioned on cloud infrastructure",
          "Per-session VPN certificates for isolated, secure access",
          "Automatic teardown keeps environments clean and costs low",
          "Live status telemetry for every running instance",
        ]}
        cta="Explore the catalog"
        ctaHref="/register"
        visual={<HeroVisual />}
      />

      <Spotlight
        id="topology"
        reverse
        eyebrow="AI Topology Designer"
        title={
          <>
            Describe a network — watch it{" "}
            <span className="lp-highlight">build itself</span>
          </>
        }
        body="The topology chatbot turns plain-language intent into a validated, editable network on an interactive canvas. Iterate by conversation, then save and reuse your designs across scenarios."
        points={[
          "Natural-language to network topology, powered by an LLM agent",
          "Interactive drag-and-edit canvas with device-type awareness",
          "Version-aware threads with time-travel over your design history",
          "Save, template, and reload topologies across your team",
        ]}
        cta="Try the designer"
        ctaHref="/register"
        visual={<TopologyVisual />}
      />
    </>
  );
}
